import FilterUtils, { FilterArgument } from "./utils/FilterUtils";
import Table, { Table$ColumnMoveEvent, Table$ColumnResizeEvent } from "sap/ui/table/Table";
import BaseController from "sap/mcsuite/controller/BaseController";
import PersonalizableSubtask from "./PersonalizableSubtask";
import ListUtils from "./utils/ListUtils";
import ExportUtils, { ExportParams } from "./utils/ExportUtils";
import ODataListBinding from "sap/ui/model/odata/v4/ODataListBinding";
import Label from "sap/m/Label";
import Button from "sap/m/Button";
import Filter from "sap/ui/model/Filter";
import { Dialog$BeforeOpenEvent } from "sap/m/Dialog";
import JSONModel from "sap/ui/model/json/JSONModel";
import Column from "sap/ui/table/Column";
import LogHandler from "../handlers/LogHandler";

export default class List extends PersonalizableSubtask {
	private params: ListCreationParams;
	private filters: {
		columnFilter: Filter[];
		initialFilter: Filter[];
	} = {
		columnFilter: [],
		initialFilter: [],
	};
	protected eventHandlers = {
		export: this.attachExportEvent.bind(this),
		copy: this.attachCopyEvent.bind(this),
		personalization: this.attachPersonalizationEvent.bind(this),
		emptyTable: this.attachEmptyTableEvent.bind(this),
		columnMove: this.attachColumnMoveEvent.bind(this),
		columnResize: this.attachColumnResizeEvent.bind(this),
		savePersonalization: this.attachSavePersonalizationEvent.bind(this),
	};

	protected personalizationData: ListPersonalizationData = {};

	/**
	 * Creates an instance of List.
	 * @param {BaseController} controller the controller that manages this subtask
	 * @param {string} subtaskId the ID of the subtask
	 * @param {ListCreationParams} params the parameters for list creation, including optional filter and showEmptyTable
	 */
	constructor(
		controller: BaseController,
		subtaskId: string,
		params: ListCreationParams,
	) {
		super(controller, subtaskId);
		this.metadata = {
			name: "List",
			subtaskId: subtaskId,
		};
		this.params = params;

		this.getTable().setBusy(true);
		this.waitForBindingReady(this.getTable(), "rows", () => {
			this.onListBindingReady();
		});
	}

	public onListBindingReady(): void {
		this.initPersonalization();
		this.buildFilter();
		ListUtils.buildColumnMenus(this);
		this.attachHandlers();
		this.getTable().setBusy(false);
	}

	/**
	 * Attaches the personalization event to the list.
	 * This method is intended to be overridden by subclasses to implement specific personalization logic.
	 */
	private attachPersonalizationEvent(): void {
		const personalizationButton = this.getUIElementByNameInSubtask(
			"personalizationButton",
		) as Button;
		personalizationButton.attachPress(() => {
			this.openPersonalizationDialog(
				this.onPersonalizationDialogBeforeOpen.bind(this),
				this.onPersonalizationDialogAfterClose.bind(this),
			);
		});
	}

	private attachColumnResizeEvent(): void {
		this.getTable().attachColumnResize((event: Table$ColumnResizeEvent) => {
			const column = event.getParameter("column");
			const width = event.getParameter("width");
			if (!column || !width) {
				console.warn("Column or width is not defined in column resize event.");
				return;
			}
			const colName = column.getFilterProperty();
			if (colName) {
				const personalizationColumn = (this.personalizationData.columns || []).find(
					(col) => col.name === colName,
				);
				if (personalizationColumn) {
					personalizationColumn.width = width; // Update the width in personalization data
				}
			}
		});
	}

	private attachColumnMoveEvent(): void {
		this.getTable().attachColumnMove((event: Table$ColumnMoveEvent) => {
			// iterate over columns and set the position data attribute
			const movedColumn = event.getParameter("column");
			const newPos = event.getParameter("newPos");
			const columns = this.getTable().getColumns();
			this.calculateColumnPositions(columns, movedColumn, newPos);
			this.applyPersonalization();
		});
	}

	private calculateColumnPositions(
		columns: Column[],
		movedColumn: Column,
		newPos: number,
	): void {
		// After the move, update all positions to match the new order in the table
		const reorderedColumns = [...columns];
		const oldIndex = reorderedColumns.indexOf(movedColumn);
		if (oldIndex === -1) return;

		// Remove the moved column and insert it at the new position
		reorderedColumns.splice(oldIndex, 1);
		reorderedColumns.splice(newPos, 0, movedColumn);

		// Update personalizationData positions to match the new order
		reorderedColumns.forEach((column, idx) => {
			this.saveNewColumnPosition(column, idx);
		});
	}

	private saveNewColumnPosition(column: Column, position: number): void {
		const colName = column.getFilterProperty();
		column.data("position", position); // Corrected variable name from 'pos' to 'position'
		if (colName) {
			const personalizationColumn = (this.personalizationData.columns || []).find(
				(col) => col.name === colName,
			);
			if (personalizationColumn) {
				personalizationColumn.position = position; // Update the position in personalization data
			}
		}
	}

	private attachSavePersonalizationEvent(): void {
		const saveButton = this.getUIElementByNameInSubtask(
			"savePersonalizationButton",
		) as Button;
		saveButton.attachPress(() => {
			this.applyPersonalization();
			this.savePersonalization();
		});
	}

	public getController(): BaseController {
		return this.controller;
	}
	/**
	 * Attaches the export event to the list.
	 * This method is intended to be overridden by subclasses to implement specific export logic.
	 */
	private attachExportEvent(): void {
		const exportParams = this.buildExportParams(false); // Default to clipboard export
		const exportButton = this.getUIElementByNameInSubtask(
			"exportButton",
		) as Button;

		exportButton.attachPress(() => {
			ExportUtils.export(exportParams)
				.then(() => {
					LogHandler.display("Export completed successfully.");
				})
				.catch((error) => {
					console.error("Error during export:", error);
				});
		});
	}

	/**
	 * Attaches the empty table event to the list.
	 */
	private attachEmptyTableEvent(): void {
		LogHandler.display("Empty table event attached");
	}

	/**
	 * Builds the export parameters for the list.
	 * @param {boolean} toClipboard - Whether to export to clipboard or not.
	 * @returns {ExportParams} The export parameters.
	 */
	private buildExportParams(toClipboard: boolean): ExportParams {
		const expandParams =
			(this.getListBinding().getQueryOptions().$expand as Record<
				string,
				unknown
			>) || {};
		return {
			oDataListBinding: this.getListBinding(),
			expand: Object.keys(expandParams),
			toClipboard: toClipboard,
			columnHeaderMap: this.getHeaderMap(),
		};
	}
	/**
	 * Attaches the copy event to the list.
	 */
	private attachCopyEvent(): void {
		const exportParams = this.buildExportParams(true); // Set toClipboard to true
		const copyButton = this.getUIElementByNameInSubtask("copyButton") as Button;
		copyButton.attachPress(() => {
			ExportUtils.export(exportParams)
				.then(() => {
					LogHandler.display("Copy to clipboard completed successfully.");
				})
				.catch((error) => {
					console.error("Error during copy to clipboard:", error);
				});
		});
	}

	private getHeaderMap(): Record<string, string> {
		const table = this.getTable();
		const columns = table.getColumns();
		const headerMap: Record<string, string> = {};
		columns.forEach((column) => {
			const filterProperty = column.getFilterProperty();
			if (filterProperty) {
				headerMap[filterProperty] =
					(column.getLabel() as Label)?.getText() || filterProperty;
			}
		});
		return headerMap;
	}

	/**
	 * Returns the UI element of the list.
	 * @returns {Table} The table UI element associated with this list subtask.
	 */
	public getTable(): Table {
		const table = this.controller
			.getView()
			.byId(`${this.subtaskId}-table`) as Table;
		if (!table) {
			console.error(`Table with ID ${this.subtaskId}-table not found.`);
			throw new Error(`Table with ID ${this.subtaskId}-table not found.`);
		}
		return table;
	}

	/**
	 * Returns the ODataListBinding for the list.
	 * This method retrieves the binding from the table and ensures it is an instance of ODataListBinding.
	 * If the binding is not found or is not valid, it logs an error and throws an exception.
	 * @returns {ODataListBinding} The ODataListBinding associated with this list subtask.
	 * @throws {Error} If the table or binding is not valid.
	 */
	public getListBinding(): ODataListBinding {
		const table = this.getTable();
		if (!(table instanceof Table)) {
			console.error(
				`Table with ID ${this.subtaskId}-table is not a valid Table instance.`,
			);
			throw new Error(
				`Table with ID ${this.subtaskId}-table is not a valid Table instance.`,
			);
		}

		const binding = table.getBinding("rows") as ODataListBinding;
		if (!(binding instanceof ODataListBinding)) {
			console.error(
				`Binding for table with ID ${this.subtaskId}-table is not a valid ODataListBinding.`,
			);
			throw new Error(
				`Binding for table with ID ${this.subtaskId}-table is not a valid ODataListBinding.`,
			);
		}
		if (!binding) {
			console.error(
				`No binding found for table with ID ${this.subtaskId}-table.`,
			);
			throw new Error(
				`No binding found for table with ID ${this.subtaskId}-table.`,
			);
		}

		return binding;
	}

	/**
	 * Builds the filter for the list based on the provided parameters.
	 * If no filter is provided, it does nothing.
	 * If the table binding is not found or is not a valid ListBinding, it logs an error.
	 */
	private buildFilter(): void {
		if (!this.params || !this.params.filter) {
			return;
		}
		const filter = FilterUtils.buildFilter(this.params.filter, true);
		this.filters.initialFilter.push(filter);
		this.getListBinding().filter(filter);
	}

	/**
	 * Adds a column filter to the list.
	 * This method allows adding a filter that will be applied to the list binding.
	 * @param {Filter} filter - The filter to be added.
	 * @returns {void}
	 * @public
	 */
	public addColumnFilter(filter: Filter): void {
		this.filters.columnFilter.push(filter);
	}

	/**
	 * Resets all column filters to an empty state.
	 * This method clears the column filters and re-applies the initial filters to the list binding.
	 * It does not affect the initial filters.
	 * @returns {void}
	 * @public
	 */
	public resetColumnFilters(): void {
		this.filters.columnFilter = [];
		this.applyFilters();
	}

	/**
	 * Applies all filters to the list binding.
	 * This method combines initial filters and column filters, then applies them to the list binding.
	 * @returns {void}
	 * @public
	 */
	public applyFilters(): void {
		const allFilters = [
			...this.filters.initialFilter,
			...this.filters.columnFilter,
		];
		this.getListBinding().filter(allFilters);
	}

	/**
	 * Removes a column filter from the list.
	 * @param filter the filter to remove from the column filters.
	 * If a string is provided, it will remove the filter with the matching path.
	 * @returns {void}
	 * @public
	 */
	public removeColumnFilter(filter: Filter | string): void {
		if (typeof filter === "string") {
			this.filters.columnFilter = this.filters.columnFilter.filter(
				(f) => f.getPath() !== filter,
			);
		} else {
			this.filters.columnFilter = this.filters.columnFilter.filter(
				(f) => f !== filter,
			);
		}
		this.applyFilters();
	}

	public onPersonalizationDialogAfterClose(): void {}

	public onPersonalizationDialogBeforeOpen(
		event: Dialog$BeforeOpenEvent,
	): void {
		// load personalization data into table> model
		const dialog = event.getSource();
		dialog.setModel(new JSONModel(this.personalizationData), "table");
	}

	public applyPersonalization(): void {
		this.applyPersonalizationToColumns();
		this.sortColumnsByPosition();
	}

	private applyPersonalizationToColumns(): void {
		const currentColumns = this.getTable().getColumns();
		for (const column of currentColumns) {
			this.applyPersonalizationToColumn(column);
		}
	}

	private applyPersonalizationToColumn(column: Column): void {
		const colName = column.getFilterProperty();
		column.data("position", Infinity); // Default position to Infinity
		if (!colName) {
			return; // Skip columns without a filter property
		}
		if (
			!(this.personalizationData.columns || []).some(
				(col) => col.name === colName,
			)
		) {
			return; // Skip columns not in personalization data
		}
		const colData = (this.personalizationData.columns || []).find(
			(col) => col.name === colName,
		);
		if (colData) {
			column.setVisible(colData.visible);
			column.setWidth(colData.width || "auto"); // Set width if provided, otherwise default
			column.data(
				"position",
				colData.position !== undefined ? colData.position : Infinity,
			); // Set position or default to Infinity
		}
	}

	private sortColumnsByPosition(): void {
		const sortedColumns = this.getTable()
			.getColumns()
			.sort((a, b) => {
				const posA = (a.data("position") as number) || Infinity;
				const posB = (b.data("position") as number) || Infinity;
				return posA - posB;
			});
		this.getTable().removeAllColumns();
		sortedColumns.forEach((column) => {
			this.getTable().addColumn(column);
		});
	}

	/**
	 * Creates default personalization data for the List subtask.
	 * Overrides the method from PersonalizableSubtask to provide specific data structure for List.
	 * @return {ListPersonalizationData} The default personalization data for the List subtask.
	 * @public
	 */
	public createDefaultPersonalizationData(): ListPersonalizationData {
		console.warn("Creating default personalization data for List subtask.");
		return {
			
			columns: this.getTable()
			.getColumns()
			.map((column, index) => {
				const translationInfo = (column.getLabel() as Label)?.getBindingInfo("text") as { parts: { value: string }[] };
				// don't return column if translationInfo is not available
				if (!translationInfo){
					console.warn(`Column ${column.getId()} does not have translation info.`);
					return null; // Skip this column
				}
				return {
					name: column.getFilterProperty() || "",
					visible: column.getVisible(),
					position: index,
					translation: {
						table: translationInfo.parts[0].value, // Use the first part's value as the translation key
						field: translationInfo.parts[1].value, // Use the second part's value if available
					},
					width: column.getWidth() || "auto", // Default width if not provided
				};
			}).filter(
				(column) => column !== null,
			),
			columnFilters: [],
		};
	}
}

export type ListCreationParams = {
	showEmptyTable?: string;
	filter?: FilterArgument[];
};

export type ListPersonalizationData = {
	columns?: {
		name: string;
		translation?: {
			table: string;
			field: string;
		};
		visible: boolean;
		position: number;
		width?: string;
	}[];
	columnFilters?: FilterArgument[];
};
